import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import IcomoonReact from 'icomoon-react';
import Select from 'react-select';
import { useSpring, animated } from 'react-spring';
import { Mentions } from 'antd';
import PostActionButtons from '../components/presentational/PostActionButtons';
import TBButton from '../../../shared/components/Button/Button';
import iconSet from '../../../shared/images/teambuildr-selection.json';
import FeedGiphyEmoji from '../components/presentational/FeedGiphyEmoji';
import CreateEditEntryModal from '../../../shared/_TBComponents/CreateEditEntryModal';

import useFeed from '../components/hooks/useFeed';
import { setUploadMedia, setUploadMediaFalse } from '../ducks/feedActions';
import {
  urlSnapper,
  userParser,
  createMentionSearchValidator,
  buildSubmissionObject,
  findFeedIndex,
  dateCreator,
  replaceMentionsInMessage,
  extractMentionsFromText,
  filterUserMentions,
  transformPostText,
} from '../helperFunctions';
import { feedTracker } from '../../../shared/utils/amplitudeHelper';

const AnimatedLittleBoxDiv = styled(animated.div)`
  padding: 7px 0px;
  border-right: 2px solid #f0f0f0;
  width: 100px;
  display: flex;
  justify-content: flex-start;
  overflow: hidden;
`;

const AnimatedMediaBoxDiv = styled(animated.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-top: 0;
  margin-left: 0px;
  left: 0px;
  min-height: 75px;
  min-width: 75px;
  max-height: 75px;
  max-height: 75px;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #495057;
  background-image: ${(props) => `url(${props.image})`};
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  border: 0px solid #ced4da;
  border-radius: 50%;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
`;

const AnimatedSelectBoxDiv = styled(animated.div)`
  display: flex;
  width: 100%;
  background: #ffffff;
  .group-select-box {
    width: 100%;
  }
`;

const ArchButtonWrapper = styled('div')`
  display: flex;
  align-items: center;
`;

const ButtonCircle = styled('div')`
  display: flex;
  color: #9e9e9e;
  justify-content: center;
  align-items: center;
  border: ${(props) => (props.isClicked ? '1px solid #0dcc8a' : '1px solid #9e9e9e')};
  border-radius: 50%;
  cursor: ${(props) => (props.isEdit ? 'not-allowed' : 'pointer')};
  padding: 5px;
  background: ${(props) => (props.isClicked ? '#0dcc8a' : 'none')};
  min-width: 38px;
  min-height: 38px;
`;

const ButtonsContainer = styled('div')`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
`;

const GroupSelectBox = styled('div')`
  width: 100%;
  border-top-width: 1px;
  border-top-style: solid;
  border-top-color: #eeeeee;
  .left-title {
    display: flex;
    justify-content: center;
    padding: 10px;
    width: 145px;
    min-width: 145px;
    border-right-width: 1px;
    border-right-style: solid;
    border-right-color: #eeeeee;
  }
`;

const MediaBox = styled('div')`
  display: flex;
  justify-content: space-between;
  background: #ffffff;
  padding: 8px 0px;
  .remove-media {
    display: ${(props) => (props.uploadMedia ? 'flex' : 'none')};
    border-radius: 50%;
    height: 20px;
    width: 20px;
    position: absolute;
    left: 55px;
    z-index: 1;
    top: -5px;
    background: #d6d6d6;
  }
`;

const getCustomStyles = (isEdit) => {
  if (isEdit) {
    return {
      container: (provided) => ({
        ...provided,
        border: 'none',
      }),
      control: (provided) => ({
        ...provided,
        border: 'none',
        cursor: 'default',
        background: '#ffffff',
      }),
      menu: (provided) => ({
        ...provided,
        display: 'none',
      }),
      option: (styles) => ({
        ...styles,
        paddingRight: '6px',
      }),
      multiValueRemove: (styles) => ({
        ...styles,
        display: 'none',
      }),
      multiValue: (styles) => ({
        ...styles,
        paddingRight: '6px',
      }),
      indicatorsContainer: (styles) => ({
        ...styles,
        display: 'none',
      }),
    };
  }
  return {
    control: (provided) => ({
      ...provided,
      border: 'none',
      cursor: 'pointer',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 99,
    }),
    option: (styles) => ({
      ...styles,
      cursor: 'pointer',
    }),
  };
};

const FeedPostModal = ({ onRequestClose, isEdit }) => {
  // Custom hooks
  const { handleCreateFeedPost, handleEditPost } = useFeed();

  // Redux hooks
  const dispatch = useDispatch();
  const groups = useSelector((state) => state.feed.data.groups);
  const users = useSelector((state) => state.feed.data.users);
  const uploadMedia = useSelector((state) => state.feed.data.uploadMedia);
  const isMediaLoading = useSelector((state) => state.feed.ui.isMediaLoading);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedGroups = useSelector((state) => state.feed.data.selectedGroups);
  const editObject = useSelector((state) => state.feed.data.editObject);
  const feed = useSelector((state) => state.feed.data.feed);
  const pinnedFeed = useSelector((state) => state.feed.data.pinnedFeed);

  // React hooks
  const [localSelectedGroups, setLocalSelectedGroups] = useState(
    selectedGroups,
  );
  const [isGroupClicked, setIsGroupClicked] = useState(
    isEdit
      ? editObject.groupAccess === null || !!editObject.groupAccess.length
      : !!selectedGroups.length,
  );
  const [userMentions, setUserMentions] = useState([]);
  const isSubmittingRef = useRef(false);
  const groupSelectRef = useRef(false);

  const gradientColors = currentUser?.userSkin?.gradientColors;
  const currentUserName = [currentUser?.firstName || currentUser?.first,
    currentUser?.lastName || currentUser?.last].filter(Boolean).join(' ');

  // Animation hooks
  const selectBox = useSpring({
    minHeight: isGroupClicked ? '39px' : '0px',
    maxHeight: isGroupClicked ? '300px' : '0px',
  });
  const mediaBoxStyle = useSpring({ height: uploadMedia ? '100px' : '0px' });

  // Computed values
  const boxFlipper = () => {
    if (isEdit) {
      return editObject.groupAccess || [{ id: 0, name: 'All Feeds' }];
    }
    return localSelectedGroups;
  };

  const customStyles = getCustomStyles(isEdit);

  const displayDate = dateCreator(isEdit ? editObject.createdAt : null);

  const initialValues = isEdit
    ? {
      body: editObject.postText || '',
      commentsDisabled: !editObject.replyAllowed,
      pinValue: editObject.pinnedUntil
        ? { id: editObject.pinnedUntil }
        : null,
    }
    : {
      body: '',
      commentsDisabled: false,
      pinValue: null,
    };

  const submitDisabled = (values, submitting, mediaLoading, submittingRef) => (
    !values.body.trim().length && !uploadMedia)
    || submitting
    || mediaLoading
    || submittingRef.current
    || (values.pinValue !== null && values.pinValue.id === null);

  // Handler functions
  const handleCloseModal = () => {
    isSubmittingRef.current = false;
    onRequestClose();
  };

  const handleGroupSelectClick = () => {
    if (isEdit) {
      return;
    }
    if (isGroupClicked) {
      setIsGroupClicked(false);
      setLocalSelectedGroups([]);
    } else {
      setIsGroupClicked(true);
    }
  };

  const handleMentionsChange = (string, setFieldValue) => {
    setFieldValue('body', string);
    filterUserMentions(userMentions, setUserMentions, string);
  };

  const handleMentionsSelect = (selection) => {
    if (selection && selection.value) {
      // Find the user that matches the selected value
      const selectedUser = users.find((user) => `${user.first} ${user.last}` === selection.value);

      if (selectedUser) {
        const submissionValue = `[u=${selectedUser.id}|${selectedUser.first} ${selectedUser.last}]`;
        setUserMentions((prevUserMentions) => [
          ...prevUserMentions,
          submissionValue,
        ]);
      }
    }
  };

  const handleRemoveMedia = () => {
    dispatch(setUploadMediaFalse());
  };

  const handleSelectGroups = (options) => {
    setLocalSelectedGroups(options);
  };

  const handleSubmit = (values) => {
    const { body } = values;
    const processedText = body
      ? replaceMentionsInMessage(body, userMentions)
      : '';

    const additionalProps = {};
    if (localSelectedGroups.length) {
      const localSelectedGroupIds = localSelectedGroups.map(
        (group) => group.id,
      );
      additionalProps.groupIds = localSelectedGroupIds;
    }
    if (values.pinValue) {
      additionalProps.pinUntil = values.pinValue.id;
    }
    if (values.commentsDisabled) {
      additionalProps.disableReplies = true;
    }
    const submissionOb = buildSubmissionObject(
      processedText,
      uploadMedia,
      additionalProps,
    );

    if (isEdit) {
      let oldFeedIndex = null;
      let feedIndex = null;
      let isUnpinning = false;
      feedIndex = findFeedIndex(editObject, feed, pinnedFeed);
      if (!editObject.pinned && values.pinValue) {
        oldFeedIndex = feed.findIndex(
          (feedItem) => feedItem.id === editObject.id,
        );
      }
      if (editObject.pinned && !values.pinValue) {
        isUnpinning = true;
      }
      handleEditPost({
        accountCode: currentUser.accountCode,
        submissionOb,
        feedPostId: editObject.id,
        feedIndex,
        oldFeedIndex,
        isUnpinning,
      });
      feedTracker('Normal Mode', 'Feed post edited');
    } else {
      handleCreateFeedPost(currentUser.accountCode, submissionOb);
      feedTracker('Normal Mode', 'Feed post created');
    }
  };

  // useEffect hooks
  useEffect(() => {
    if (isEdit) {
      const mentions = extractMentionsFromText(editObject.postText);
      setUserMentions(mentions);
    }
  }, [isEdit, editObject.postText]);

  useEffect(() => {
    if (isEdit && editObject?.media && Object.keys(editObject.media).length) {
      dispatch(setUploadMedia(editObject.media));
    }
  }, [isEdit, editObject?.media]);

  // Render functions
  const renderPostActions = (values, setFieldValue) => (
    <ArchButtonWrapper>
      <ButtonCircle
        isClicked={isGroupClicked}
        isEdit={isEdit}
        onClick={handleGroupSelectClick}
      >
        <IcomoonReact
          iconSet={iconSet}
          size={20}
          icon='group'
          color={isGroupClicked ? '#ffffff' : '#9e9e9e'}
        />
      </ButtonCircle>
      <PostActionButtons
        setFieldValue={setFieldValue}
        values={values}
        editObject={isEdit ? editObject : {}}
      />
    </ArchButtonWrapper>
  );

  const renderGroupSelectSection = () => (
    <GroupSelectBox isGroupClicked={isGroupClicked}>
      <AnimatedSelectBoxDiv style={selectBox} ref={groupSelectRef}>
        <AnimatedLittleBoxDiv>
          {isEdit ? 'Posted To:' : 'Post To:'}
        </AnimatedLittleBoxDiv>
        <Select
          className='group-select-box'
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.id}
          options={groups}
          onChange={handleSelectGroups}
          isClearable={!isEdit}
          placeholder='Select Groups'
          isSearchable
          styles={customStyles}
          isMulti
          isDisabled={isEdit}
          value={boxFlipper()}
        />
      </AnimatedSelectBoxDiv>
    </GroupSelectBox>
  );

  const renderInputSection = (values, setFieldValue) => (
    <Mentions
      validateSearch={createMentionSearchValidator(users)}
      className='text-area'
      placement='bottom'
      placeholder='Write your post here...'
      value={isEdit ? transformPostText(values.body) : values.body}
      onChange={(string) => {
        handleMentionsChange(string, setFieldValue);
      }}
      onSelect={handleMentionsSelect}
    >
      {users && !!users.length && userParser(users)}
    </Mentions>
  );

  const renderMediaGiphyEmojiSection = (values, setFieldValue) => (
    <MediaBox uploadMedia={uploadMedia} image={urlSnapper(uploadMedia)}>
      <AnimatedMediaBoxDiv
        style={mediaBoxStyle}
        image={urlSnapper(uploadMedia)}
      >
        <TBButton
          icon='remove'
          iconOnly
          square
          onClick={handleRemoveMedia}
          className='remove-media'
        />
      </AnimatedMediaBoxDiv>
      <ButtonsContainer>
        <FeedGiphyEmoji
          setFieldValue={setFieldValue}
          values={values}
          fieldName='body'
        />
      </ButtonsContainer>
    </MediaBox>
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, { setSubmitting }) => {
        handleSubmit(values);
        setSubmitting(false);
        isSubmittingRef.current = false;
      }}
    >
      {({
        values, setFieldValue, isSubmitting, submitForm,
      }) => (
        <CreateEditEntryModal
          currentUserName={currentUserName}
          customPadding='20px 20px 14px 20px'
          displayDate={displayDate}
          gradientColors={gradientColors}
          isEditMode={isEdit}
          onClose={handleCloseModal}
          onSubmit={() => {
            if (isSubmittingRef.current) return;
            isSubmittingRef.current = true;
            submitForm();
          }}
          postActions={renderPostActions(values, setFieldValue)}
          submitDisabled={submitDisabled(values, isSubmitting, isMediaLoading, isSubmittingRef)}
          userPic={currentUser?.pic}
        >
          {renderInputSection(values, setFieldValue)}
          {renderMediaGiphyEmojiSection(values, setFieldValue)}
          {renderGroupSelectSection()}
        </CreateEditEntryModal>
      )}
    </Formik>
  );
};

FeedPostModal.propTypes = {
  onRequestClose: PropTypes.func.isRequired,
  isEdit: PropTypes.bool,
};

FeedPostModal.defaultProps = {
  isEdit: false,
};

export default FeedPostModal;
